import { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { GlassCard } from './Layout';
import { Compass, Check, X, AlertTriangle, ArrowRight, Link2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/life-design`;

const money = (n) => (n || n === 0 ? `$${Math.round(n).toLocaleString()}` : '-');
const FIT_HUE = { strong: '#34d399', partial: '#fbbf24', weak: '#f87171' };

/** The join between who they are and the life they said they want. */
export const LifeDesignIntelligence = () => {
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(API, getAuthHeader());
      setD(r.data);
    } catch { /* additive */ }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  const v = d.target_verdict;

  return (
    <div className="space-y-6" data-testid="life-design-intelligence">
      {/* what this is built from */}
      <GlassCard className="fp-rail">
        <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-1">
          <Link2 className="w-4 h-4 text-cyan-300" /> This page reads from the rest of your work
        </h3>
        <p className="text-xs text-slate-300 mb-4">{d.note}</p>
        <div className="grid md:grid-cols-3 gap-3">
          {d.built_from.map(b => (
            <Link key={b.label} to={b.path} className="fp-tile p-3 block" data-testid={`built-from-${b.label}`}>
              <div className="flex items-center gap-2">
                {b.ready
                  ? <Check className="w-3.5 h-3.5 text-emerald-400" />
                  : <X className="w-3.5 h-3.5 text-slate-300" />}
                <span className="text-[11px] uppercase tracking-wider text-slate-300">{b.label}</span>
              </div>
              <p className={`text-sm mt-1 ${b.ready ? 'text-white' : 'text-slate-300'}`}>{b.value}</p>
            </Link>
          ))}
        </div>
      </GlassCard>

      {/* the verdict on their own target */}
      {v && (
        <GlassCard data-testid="life-design-verdict">
          <div className="flex flex-wrap items-start gap-6">
            <div className="flex-1 min-w-[260px]">
              <p className="text-[11px] uppercase tracking-wider text-slate-300">Your target, judged on both tests</p>
              <h3 className="text-lg md:text-xl font-semibold text-white mt-1">{v.career}</h3>
              <p className="text-sm text-slate-300 mt-2">{v.headline}</p>
              {v.fit_note && <p className="text-xs text-slate-300 mt-2">{v.fit_note}</p>}
            </div>
            <div className="grid grid-cols-3 gap-5">
              <div><p className="text-[10px] uppercase tracking-wider text-slate-300">Median pay</p>
                <p className="text-xl font-bold text-white fp-num">{money(v.median)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-slate-300">Your floor</p>
                <p className="text-xl font-bold text-cyan-300 fp-num">{money(v.income_floor)}</p></div>
              <div><p className="text-[10px] uppercase tracking-wider text-slate-300">Interest fit</p>
                <p className="text-xl font-bold fp-num" style={{ color: FIT_HUE[v.fit_band] || '#94a3b8' }}>
                  {v.fit_band || '-'}</p></div>
            </div>
          </div>
        </GlassCard>
      )}

      {/* careers that pass money AND fit */}
      {d.fit_and_affordable?.length > 0 && (
        <GlassCard data-testid="fit-and-affordable">
          <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-1">
            <Compass className="w-4 h-4 text-emerald-300" /> Fits you and pays for the life you described
          </h3>
          <p className="text-xs text-slate-300 mb-4">
            Both tests, not one: your {d.assessment?.code} interest code and your own income floor.
          </p>
          <div className="grid md:grid-cols-3 gap-3">
            {d.fit_and_affordable.map(c => (
              <Link key={c.soc_code + c.title} to={`/app/careers?q=${encodeURIComponent(c.title)}`}
                className="fp-tile p-3 block" data-testid={`fit-afford-${c.soc_code}`}>
                <p className="text-sm text-white">{c.title}</p>
                <p className="text-[11px] text-slate-300">{c.field}</p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className="text-sm fp-num text-white">{money(c.median)}</span>
                  {c.clears_floor === true && <span className="text-[10px] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-300">clears your floor{c.gap ? ` +${money(c.gap)}` : ''}</span>}
                  {c.clears_floor === false && <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-500/20 text-rose-300">under your floor</span>}
                  {c.fit && <span className="text-[10px] px-2 py-0.5 rounded-full" style={{ background: `${FIT_HUE[c.fit]}22`, color: FIT_HUE[c.fit] }}>{c.fit} fit</span>}
                  {c.ai_exposure != null && <span className="text-[10px] text-slate-300">AI {c.ai_exposure}</span>}
                </div>
              </Link>
            ))}
          </div>
        </GlassCard>
      )}

      <div className="grid lg:grid-cols-2 gap-6">
        {d.environment_conflicts?.length > 0 && (
          <GlassCard data-testid="environment-conflicts">
            <h3 className="text-base md:text-lg font-semibold text-white flex items-center gap-2 mb-3">
              <AlertTriangle className="w-4 h-4 text-amber-300" /> Where you'd be unhappy regardless of pay
            </h3>
            <ul className="space-y-2">
              {d.environment_conflicts.map(c => (
                <li key={c.key} className="text-sm text-amber-100/90 flex gap-2"><span className="text-amber-400">-</span>{c.note}</li>
              ))}
            </ul>
          </GlassCard>
        )}

        {d.roadmap_seeds?.length > 0 && (
          <GlassCard data-testid="roadmap-seeds">
            <h3 className="text-base md:text-lg font-semibold text-white mb-1">What this makes your next step</h3>
            <p className="text-xs text-slate-300 mb-3">These feed straight into your roadmap.</p>
            <div className="space-y-2">
              {d.roadmap_seeds.map((s, i) => (
                <Link key={i} to={s.path} className="fp-tile p-3 block group" data-testid={`seed-${i}`}>
                  <p className="text-sm text-white">{s.action}</p>
                  <p className="text-xs text-slate-300 mt-0.5">{s.why}</p>
                  <span className="mt-2 inline-flex items-center gap-1 text-xs text-cyan-300 group-hover:gap-2 transition-all duration-200">
                    Go there <ArrowRight className="w-3 h-3" />
                  </span>
                </Link>
              ))}
            </div>
          </GlassCard>
        )}
      </div>
    </div>
  );
};

/** Compact strip for the roadmap: everything the plan is built from. */
export const BuiltFromStrip = () => {
  const { getAuthHeader } = useAuth();
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(API, getAuthHeader());
      setD(r.data);
    } catch { /* additive */ }
  }, [getAuthHeader]);

  useEffect(() => { load(); }, [load]);
  if (!d) return null;

  return (
    <div className="mb-6 p-4 rounded-xl border border-slate-700 bg-slate-900/50" data-testid="built-from-strip">
      <p className="text-[11px] uppercase tracking-wider text-slate-300 mb-3">This plan is built from</p>
      <div className="flex flex-wrap gap-2">
        {d.built_from.map(b => (
          <Link key={b.label} to={b.path} className="fp-pill" data-testid={`strip-${b.label}`}
            style={b.ready ? { borderColor: '#34d39966', color: '#e2e8f0' } : { borderColor: 'rgba(148,163,184,.3)', color: '#94a3b8' }}>
            {b.ready ? <Check className="w-3 h-3 text-emerald-400" /> : <X className="w-3 h-3" />}
            {b.label}: {b.value}
          </Link>
        ))}
        {d.roadmap_seeds?.[0] && (
          <span className="fp-pill" style={{ borderColor: '#22d3ee66' }}>
            Next: {d.roadmap_seeds[0].action}
          </span>
        )}
      </div>
    </div>
  );
};

export default LifeDesignIntelligence;
